/**
 * Curated enum option lists for column filters. Each entry exposes:
 *
 *   - `value` — the storage-layer code that the backend filter receives
 *     (e.g. MIC codes like `'XNAS'` for exchange).
 *   - `label` — what the user sees in the dropdown / selection chip.
 *
 * Most lists are label = value (sectors, dilution risk, etc.). The share-group
 * codes intentionally diverge so the user picks "Common Stock" while the
 * backend receives `'CS'`.
 *
 * Yes/No flags backed by INTEGER 0/1 in the warehouse (live `ssr`, `halt`,
 * historical `day*_open_ssr`) reuse `YES_NO_INTEGER_OPTIONS` below — multi-
 * select with friendly Yes/No labels but the value sent to the backend is the
 * integer-coded string `'1'` / `'0'`. Mirrors legacy `integerToBooleanOptions`
 * (apps/edge/src/app/slicedForm/FilterForm/definitions/dropdownOptions.js).
 */

import type { ComponentType } from 'react'

export interface EnumOption {
  value: string
  label: string
  /**
   * Optional glyph rendered before the label in dropdowns/chips. Used by
   * live-injected entity options (e.g. watchlists → the List icon) so users
   * can tell entity values apart from plain enum codes. Static lists here
   * don't set it.
   */
  icon?: ComponentType<{ className?: string }>
}

const sameLabel = (values: readonly string[]): EnumOption[] =>
  values.map((v) => ({ value: v, label: v }))

// ─── Country (ISO 2-letter codes) ───
// TODO: refresh from `country-region-data` npm package (legacy parity) so we
// stop hand-maintaining this list. Tracked in the enum-options refactor.
export const COUNTRY_ISO_OPTIONS: EnumOption[] = sameLabel([
  'US',
  'CN',
  'CA',
  'IL',
  'GB',
  'IE',
  'BM',
  'KY',
  'BR',
  'JP',
  'KR',
  'IN',
  'AU',
  'HK',
  'SG',
  'TW',
  'NL',
  'CH',
  'DE',
  'FR',
  'SE',
  'DK',
  'NO',
  'FI',
  'BE',
  'LU',
  'AR',
  'MX',
  'CL',
  'CO',
  'ZA',
  'GR',
  'IT',
  'ES',
  'PT',
  'AT',
  'NZ',
  'PH',
  'ID',
  'MY',
  'TH',
  'TR',
  'RU',
  'CY',
  'JE',
  'GG',
  'MC',
  'PA',
  'PR',
  'VI',
  'MH',
  'BS',
  'BB',
  'CW',
  'UY',
  'PE',
])

// ─── Country (long names) ───
// TODO: derive from `country-region-data` (legacy parity).
export const COUNTRY_OPTIONS: EnumOption[] = sameLabel([
  'United States',
  'China',
  'Canada',
  'Israel',
  'United Kingdom',
  'Ireland',
  'Bermuda',
  'Cayman Islands',
  'Brazil',
  'Japan',
  'South Korea',
  'India',
  'Australia',
  'Hong Kong',
  'Singapore',
  'Taiwan',
  'Netherlands',
  'Switzerland',
  'Germany',
  'France',
  'Sweden',
  'Denmark',
  'Norway',
  'Finland',
  'Belgium',
  'Luxembourg',
  'Argentina',
  'Mexico',
  'Chile',
  'Colombia',
  'South Africa',
  'Greece',
  'Italy',
  'Spain',
  'Portugal',
  'Austria',
  'New Zealand',
  'Philippines',
  'Indonesia',
  'Malaysia',
  'Thailand',
  'Turkey',
  'Russia',
  'Cyprus',
  'Jersey',
  'Guernsey',
  'Monaco',
  'Panama',
  'Puerto Rico',
  'U.S. Virgin Islands',
  'Marshall Islands',
  'Bahamas',
  'Barbados',
  'Curacao',
  'Uruguay',
  'Peru',
])

// ─── Share group ───
// Union of the original frontend list (9 codes) + DB-confirmed `PS`. We keep
// frontend codes that the DB doesn't currently emit (ADR, RT, UT, STRUCT, AD,
// PFD) on purpose — the data layer may add them later, and dropping them would
// silently break any saved preset that filters on them.
export const SHARE_GROUP_OPTIONS: EnumOption[] = [
  { value: 'CS', label: 'Common Stock' },
  { value: 'ADR', label: 'ADR' },
  { value: 'ET', label: 'Exchange Traded' },
  { value: 'WT', label: 'Warrant' },
  { value: 'RT', label: 'Right' },
  { value: 'UT', label: 'Unit' },
  { value: 'STRUCT', label: 'Structured' },
  { value: 'AD', label: 'AD' },
  { value: 'PFD', label: 'Preferred (PFD)' },
  { value: 'PS', label: 'Preferred (PS)' },
]

// ─── Exchange (MIC codes) ───
// TODO(database): MotherDuck `symbols.exchange` actually stores friendly names
// (NASDAQ, NYSE, ARCA, BATS, NYSEMKT, CBOE, AMEX) for the vast majority of
// rows, with only a tiny stray of MIC codes (XNAS:26, XNYS:2). Filtering by
// MIC therefore matches almost no rows. Fix at the DB layer (write MIC codes
// during ingest), not by accommodating the discrepancy here. Until then, the
// scanner's `exchange` filter is effectively non-functional.
export const EXCHANGE_OPTIONS: EnumOption[] = sameLabel([
  'XNAS',
  'XNYS',
  'XASE',
  'ARCX',
  'BATS',
  'IEXG',
  'XOTC',
])

export const INDUSTRY_OPTIONS: EnumOption[] = sameLabel([
  // Energy
  'Oil & Gas Drilling',
  'Oil & Gas E&P',
  'Oil & Gas Equipment & Services',
  'Oil & Gas Integrated',
  'Oil & Gas Midstream',
  'Oil & Gas Refining & Marketing',
  'Thermal Coal',
  'Uranium',
  // Basic Materials
  'Agricultural Inputs',
  'Aluminum',
  'Building Materials',
  'Chemicals',
  'Coking Coal',
  'Copper',
  'Gold',
  'Lumber & Wood Production',
  'Other Industrial Metals & Mining',
  'Other Precious Metals & Mining',
  'Paper & Paper Products',
  'Silver',
  'Specialty Chemicals',
  'Steel',
  // Industrials
  'Aerospace & Defense',
  'Airlines',
  'Airports & Air Services',
  'Building Products & Equipment',
  'Business Equipment & Supplies',
  'Conglomerates',
  'Consulting Services',
  'Electrical Equipment & Parts',
  'Engineering & Construction',
  'Farm & Heavy Construction Machinery',
  'Industrial Distribution',
  'Infrastructure Operations',
  'Integrated Freight & Logistics',
  'Marine Shipping',
  'Metal Fabrication',
  'Pollution & Treatment Controls',
  'Railroads',
  'Rental & Leasing Services',
  'Security & Protection Services',
  'Specialty Business Services',
  'Specialty Industrial Machinery',
  'Staffing & Employment Services',
  'Tools & Accessories',
  'Trucking',
  'Waste Management',
  // Communication Services
  'Advertising Agencies',
  'Broadcasting',
  'Electronic Gaming & Multimedia',
  'Entertainment',
  'Internet Content & Information',
  'Publishing',
  'Telecom Services',
  // Utilities
  'Utilities - Diversified',
  'Utilities - Independent Power Producers',
  'Utilities - Regulated Electric',
  'Utilities - Regulated Gas',
  'Utilities - Regulated Water',
  'Utilities - Renewable',
  // Real Estate
  'Real Estate - Development',
  'Real Estate - Diversified',
  'Real Estate Services',
  'REIT - Diversified',
  'REIT - Healthcare Facilities',
  'REIT - Hotel & Motel',
  'REIT - Industrial',
  'REIT - Mortgage',
  'REIT - Office',
  'REIT - Residential',
  'REIT - Retail',
  'REIT - Specialty',
])

export const SECTOR_OPTIONS: EnumOption[] = sameLabel([
  'Technology',
  'Healthcare',
  'Financial Services',
  'Consumer Cyclical',
  'Industrials',
  'Communication Services',
  'Consumer Defensive',
  'Energy',
  'Basic Materials',
  'Real Estate',
  'Utilities',
])

// ─── Dilution risk tiers (new feature, no legacy precedent) ───
export const DILUTION_OPTIONS: EnumOption[] = sameLabel(['Low', 'Medium', 'High'])

// ─── Yes/No (integer-coded boolean flags) ───
// Used by columns whose underlying storage is INTEGER 0/1 (live `ssr`,
// `halt`, and the historical `day*_open_ssr` family). The user picks the
// friendly label, the filter receives the integer-coded string '1' / '0' —
// matches legacy `integerToBooleanOptions` semantics.
export const YES_NO_INTEGER_OPTIONS: EnumOption[] = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
]
